/**
 * Admin Tickets Management Page
 *
 * Features:
 * - Create new meal tickets in batches
 * - View all tickets with filtering by status
 * - Print tickets with QR codes
 * - Void active tickets
 */

import { useState } from 'react';
import { useTickets, useCreateTickets, useVoidTicket, MealTicket } from '../../hooks/useTickets';
import { TicketsPrintView } from '../../components/TicketsPrintView';
import { apiConfig } from '../../config';

type StatusFilter = 'all' | 'active' | 'redeemed' | 'expired' | 'voided';

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = sessionStorage.getItem('hmh_access_token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

export function TicketsPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedTickets, setSelectedTickets] = useState<MealTicket[]>([]);
  const [showPrintView, setShowPrintView] = useState(false);
  const [loadingPrint, setLoadingPrint] = useState(false);
  const [selectedTicketIds, setSelectedTicketIds] = useState<Set<string>>(new Set());

  // Fetch tickets based on status filter
  const { data: tickets, isLoading, error } = useTickets(
    statusFilter === 'all' ? undefined : statusFilter
  );

  const createMutation = useCreateTickets();
  const voidMutation = useVoidTicket();

  const handleCreateTickets = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const quantity = parseInt(formData.get('quantity') as string);
    const mealType = formData.get('meal_type') as string;
    const expiresInDays = formData.get('expires_in_days') as string;
    const batchDescription = formData.get('batch_description') as string;

    try {
      const result = await createMutation.mutateAsync({
        quantity,
        meal_type: mealType,
        expires_in_days: expiresInDays ? parseInt(expiresInDays) : undefined,
        batch_description: batchDescription || undefined,
      });

      // Show newly created tickets in print view
      setSelectedTickets(result.tickets);
      setShowPrintView(true);
      setShowCreateForm(false);
      form.reset();
    } catch (err) {
      console.error('Failed to create tickets:', err);
      alert('Failed to create tickets. Please try again.');
    }
  };

  const handleVoidTicket = async (ticketId: string) => {
    if (!confirm('Are you sure you want to void this ticket? This action cannot be undone.')) {
      return;
    }

    try {
      await voidMutation.mutateAsync(ticketId);
    } catch (err) {
      console.error('Failed to void ticket:', err);
      alert('Failed to void ticket. Please try again.');
    }
  };

  const handlePrintTickets = async (ticketsToPrint: MealTicket[]) => {
    // If tickets already have QR codes (from create), use them directly
    if (ticketsToPrint.every(t => t.qr_code)) {
      setSelectedTickets(ticketsToPrint);
      setShowPrintView(true);
      return;
    }

    // Otherwise, fetch tickets with QR codes
    setLoadingPrint(true);
    try {
      const ticketsWithQr = await Promise.all(
        ticketsToPrint.map(async (ticket) => {
          const res = await fetch(
            `${apiConfig.baseUrl}/admin/tickets/${ticket.id}?include_qr=true`,
            { headers: getAuthHeaders() }
          );
          if (!res.ok) {
            throw new Error('Failed to fetch ticket with QR code');
          }
          return res.json() as Promise<MealTicket>;
        })
      );
      setSelectedTickets(ticketsWithQr);
      setShowPrintView(true);
    } catch (err) {
      console.error('Failed to load QR codes:', err);
      alert('Failed to load QR codes for printing. Please try again.');
    } finally {
      setLoadingPrint(false);
    }
  };

  // Get active tickets (printable tickets)
  const activeTickets = tickets?.filter(t => t.status === 'active') || [];

  // Toggle individual ticket selection
  const toggleTicketSelection = (ticketId: string) => {
    setSelectedTicketIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ticketId)) {
        newSet.delete(ticketId);
      } else {
        newSet.add(ticketId);
      }
      return newSet;
    });
  };

  // Toggle select all
  const toggleSelectAll = () => {
    if (selectedTicketIds.size === activeTickets.length && activeTickets.length > 0) {
      // Deselect all
      setSelectedTicketIds(new Set());
    } else {
      // Select all active tickets
      setSelectedTicketIds(new Set(activeTickets.map(t => t.id)));
    }
  };

  // Handle print selected tickets
  const handlePrintSelected = () => {
    const ticketsToPrint = activeTickets.filter(t => selectedTicketIds.has(t.id));
    if (ticketsToPrint.length > 0) {
      handlePrintTickets(ticketsToPrint);
      // Clear selection after printing
      setSelectedTicketIds(new Set());
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'redeemed':
        return 'bg-blue-100 text-blue-800';
      case 'expired':
        return 'bg-gray-100 text-gray-800';
      case 'voided':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (showPrintView) {
    return (
      <TicketsPrintView
        tickets={selectedTickets}
        onClose={() => {
          setShowPrintView(false);
          setSelectedTickets([]);
        }}
      />
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Meal Tickets</h1>
        <p className="text-gray-600">Create and manage guest meal tickets</p>
      </div>

        {/* Action Buttons */}
        <div className="mb-6 flex gap-4 items-center">
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="px-4 py-2 rounded-lg font-medium transition-colors"
            style={{ backgroundColor: '#10348e', color: 'white' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#1e62ac')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#10348e')}
          >
            {showCreateForm ? 'Cancel' : 'Create New Tickets'}
          </button>

          {selectedTicketIds.size > 0 && (
            <>
              <button
                onClick={handlePrintSelected}
                disabled={loadingPrint}
                className="px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                style={{ backgroundColor: '#10348e', color: 'white' }}
                onMouseEnter={(e) => !loadingPrint && (e.currentTarget.style.backgroundColor = '#1e62ac')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#10348e')}
              >
                Print Selected ({selectedTicketIds.size})
              </button>
              <button
                onClick={() => setSelectedTicketIds(new Set())}
                className="px-4 py-2 rounded-lg font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Clear Selection
              </button>
            </>
          )}
        </div>

        {/* Create Form */}
        {showCreateForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold mb-4" style={{ color: '#10348e' }}>
              Create New Tickets
            </h2>
            <form onSubmit={handleCreateTickets} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quantity *
                  </label>
                  <input
                    type="number"
                    name="quantity"
                    min="1"
                    max="100"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Number of tickets to create"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Meal Type *
                  </label>
                  <select
                    name="meal_type"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="any">Any Meal</option>
                    <option value="breakfast">Breakfast Only</option>
                    <option value="lunch">Lunch Only</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expires In (Days)
                  </label>
                  <input
                    type="number"
                    name="expires_in_days"
                    min="1"
                    max="365"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Leave blank for no expiration"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Batch Description
                  </label>
                  <input
                    type="text"
                    name="batch_description"
                    maxLength={100}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Board Meeting 2024"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="px-4 py-2 rounded-lg font-medium text-white transition-colors disabled:opacity-50"
                  style={{ backgroundColor: '#10348e' }}
                  onMouseEnter={(e) => !createMutation.isPending && (e.currentTarget.style.backgroundColor = '#1e62ac')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#10348e')}
                >
                  {createMutation.isPending ? 'Creating...' : 'Create Tickets'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Loading overlay for print */}
        {loadingPrint && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg text-center">
              <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-700">Loading QR codes...</p>
            </div>
          </div>
        )}

        {/* Status Filter */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-wrap gap-2">
            <span className="text-sm font-medium text-gray-700 self-center mr-2">Filter:</span>
            {(['all', 'active', 'redeemed', 'expired', 'voided'] as StatusFilter[]).map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === status
                    ? 'text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                style={
                  statusFilter === status
                    ? { backgroundColor: '#10348e' }
                    : undefined
                }
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Tickets Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading tickets...</div>
          ) : error ? (
            <div className="p-8 text-center text-red-600">
              Error loading tickets: {error.message}
            </div>
          ) : !tickets || tickets.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No tickets found. Create some tickets to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead style={{ backgroundColor: '#10348e', color: 'white' }}>
                  <tr>
                    {activeTickets.length > 0 && (
                      <th className="px-4 py-3 text-center text-sm font-semibold w-12">
                        <input
                          type="checkbox"
                          checked={selectedTicketIds.size === activeTickets.length && activeTickets.length > 0}
                          onChange={toggleSelectAll}
                          className="w-4 h-4 cursor-pointer"
                          title="Select all active tickets"
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-sm font-semibold">Code</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Meal Type</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Created</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Expires</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Redeemed By</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Batch</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {tickets.map((ticket) => (
                    <tr key={ticket.id} className="hover:bg-gray-50">
                      {activeTickets.length > 0 && (
                        <td className="px-4 py-3 text-center w-12">
                          {ticket.status === 'active' ? (
                            <input
                              type="checkbox"
                              checked={selectedTicketIds.has(ticket.id)}
                              onChange={() => toggleTicketSelection(ticket.id)}
                              className="w-4 h-4 cursor-pointer"
                            />
                          ) : (
                            <span className="w-4 h-4 inline-block"></span>
                          )}
                        </td>
                      )}
                      <td className="px-4 py-3 text-sm font-mono">{ticket.ticket_code}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                            ticket.status
                          )}`}
                        >
                          {ticket.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm capitalize">{ticket.meal_type}</td>
                      <td className="px-4 py-3 text-sm">{formatDate(ticket.created_at)}</td>
                      <td className="px-4 py-3 text-sm">{formatDate(ticket.expires_at)}</td>
                      <td className="px-4 py-3 text-sm">{ticket.redeemed_by_name || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {ticket.batch_description || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex gap-2">
                          {ticket.status === 'active' && (
                            <>
                              <button
                                onClick={() => handlePrintTickets([ticket])}
                                disabled={loadingPrint}
                                className="text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
                              >
                                Print
                              </button>
                              <button
                                onClick={() => handleVoidTicket(ticket.id)}
                                disabled={voidMutation.isPending}
                                className="text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                              >
                                Void
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
    </div>
  );
}
